import type { SponsorInput } from "@lib/db/schemas";
import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Card,
  Flex,
  Group,
  Image,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { useForm } from "@tanstack/react-form-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { MAX_UPLOAD_SIZE } from "@utils/image-config";
import { useNotification } from "@webapp/hooks/useNotification";
import {
  createSponsorFn,
  deleteSponsorFn,
  listSponsorsFn,
  updateSponsorFn,
} from "@webapp/server/functions/sponsors";
import { getFileUrlFn } from "@webapp/server/functions/upload";
import { uploadImageFile } from "@webapp/utils/upload-image";
import dayjs from "dayjs";
import { Globe, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { useState } from "react";

const bytesToMB = (bytes: number, decimals = 1) => (bytes / (1024 * 1024)).toFixed(decimals);

const defaultFormValues = {
  id: undefined as string | undefined,
  name: "",
  description: "",
  websiteUrl: "",
  logoS3Key: undefined as string | undefined,
  ttl: undefined as number | undefined,
};

function CurrentLogoDisplay({
  logoS3Key,
  logoFile,
  deleteLogo,
  onFileChange,
  onDeleteToggle,
  onFileSizeError,
}: {
  logoS3Key?: string;
  logoFile: File | null;
  deleteLogo: boolean;
  onFileChange: (file: File | null) => void;
  onDeleteToggle: () => void;
  onFileSizeError: (message: string) => void;
}) {
  const { data: logoUrl } = useQuery({
    queryKey: ["upload", "fileUrl", logoS3Key],
    queryFn: () => getFileUrlFn({ data: { s3Key: logoS3Key || "" } }),
    enabled: !!logoS3Key && !deleteLogo,
  });

  // Show new file preview if selected
  if (logoFile) {
    const previewUrl = URL.createObjectURL(logoFile);
    return (
      <Box>
        <Group justify="space-between" mb="xs">
          <Text size="sm" fw={500}>
            Logo
          </Text>
          <Button
            size="xs"
            variant="subtle"
            color="red"
            leftSection={<X size={14} />}
            onClick={() => onFileChange(null)}
          >
            Abbrechen
          </Button>
        </Group>
        <Card withBorder p="md" bg="blumine" c="white">
          <Image src={previewUrl} height={120} fit="contain" alt="Neue Logo-Vorschau" />
          <Text size="xs" mt="xs" ta="center" opacity={0.7}>
            Neues Logo: {logoFile.name}
          </Text>
        </Card>
      </Box>
    );
  }

  // Show current logo with actions if exists
  if (logoS3Key && !deleteLogo) {
    return (
      <Box>
        <Group justify="space-between" mb="xs">
          <Text size="sm" fw={500}>
            Aktuelles Logo
          </Text>
          <Group gap="xs">
            <Button
              size="xs"
              variant="light"
              leftSection={<Upload size={14} />}
              onClick={() => document.getElementById("logo-file-input")?.click()}
            >
              Ersetzen
            </Button>
            <Button
              size="xs"
              variant="light"
              color="red"
              leftSection={<X size={14} />}
              onClick={onDeleteToggle}
            >
              Löschen
            </Button>
          </Group>
        </Group>
        <Card withBorder p="md" bg="blumine">
          {logoUrl ? (
            <Image src={logoUrl} height={120} fit="contain" alt="Aktuelles Logo" />
          ) : (
            <div
              style={{
                height: 120,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text c="dimmed">Laden...</Text>
            </div>
          )}
        </Card>
        <input
          id="logo-file-input"
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              if (file.size > MAX_UPLOAD_SIZE) {
                onFileSizeError(
                  `${file.name} ist zu groß (${bytesToMB(file.size)}MB). Maximum ${bytesToMB(MAX_UPLOAD_SIZE, 0)}MB.`,
                );
                return;
              }
              onFileChange(file);
            }
          }}
        />
      </Box>
    );
  }

  // Show deletion message if logo was deleted
  if (deleteLogo && logoS3Key) {
    return (
      <Box>
        <Group justify="space-between" mb="xs">
          <Text size="sm" fw={500}>
            Logo
          </Text>
          <Button size="xs" variant="subtle" onClick={onDeleteToggle}>
            Rückgängig
          </Button>
        </Group>
        <Card withBorder p="md" bg="red.0">
          <Text size="sm" c="red" ta="center">
            Logo wird beim Speichern entfernt
          </Text>
        </Card>
      </Box>
    );
  }

  // Show Dropzone for new logo
  return (
    <Box>
      <Text size="sm" fw={500} mb="xs">
        Logo
      </Text>
      <Dropzone
        onDrop={(files: File[]) => {
          if (files.length > 0) {
            const file = files[0];
            if (file.size > MAX_UPLOAD_SIZE) {
              onFileSizeError(
                `${file.name} ist zu groß (${bytesToMB(file.size)}MB). Maximum ${bytesToMB(MAX_UPLOAD_SIZE, 0)}MB.`,
              );
              return;
            }
            onFileChange(file);
          }
        }}
        accept={IMAGE_MIME_TYPE}
        maxSize={MAX_UPLOAD_SIZE}
        maxFiles={1}
        bg="blumine"
        c="white"
        bd="1px dashed var(--mantine-color-dimmed)"
        p="xs"
      >
        <Flex
          direction={{ base: "row", md: "column" }}
          justify="center"
          align="center"
          rowGap="md"
          columnGap="md"
          mih={{ base: 80, md: 120 }}
          style={{ pointerEvents: "none" }}
        >
          <Dropzone.Accept>
            <Upload size={50} style={{ color: "var(--mantine-color-blue-6)" }} />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <X size={50} style={{ color: "var(--mantine-color-red-6)" }} />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <Upload size={50} />
          </Dropzone.Idle>

          <Stack gap="xs" align="center">
            <Text size="lg" inline>
              Logo hierher ziehen oder klicken zum Auswählen
            </Text>
            <Text size="sm" opacity={0.7} inline mt={7}>
              PNG, JPG oder SVG, max. {bytesToMB(MAX_UPLOAD_SIZE, 0)}MB
            </Text>
          </Stack>
        </Flex>
      </Dropzone>
    </Box>
  );
}

function SponsorsPage() {
  const isMobile = useMediaQuery("(max-width: 48em)");
  const [opened, { open, close }] = useDisclosure(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [deleteLogo, setDeleteLogo] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const form = useForm({
    defaultValues: defaultFormValues,
  });
  const editingId = form.getFieldValue("id");

  const notification = useNotification();
  const {
    data: sponsors,
    isLoading,
    refetch,
  } = useQuery({ queryKey: ["sponsors", "list"], queryFn: () => listSponsorsFn() });
  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof createSponsorFn>[0]["data"]) => createSponsorFn({ data }),
    onSuccess: () => {
      refetch();
      close();
      resetForm();
      setUploading(false);
      notification.success("Sponsor wurde erfolgreich erstellt");
    },
    onError: (error: unknown) => {
      setUploading(false);
      notification.error({
        message: (error as Error).message || "Sponsor konnte nicht erstellt werden",
      });
    },
  });
  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof updateSponsorFn>[0]["data"]) => updateSponsorFn({ data }),
    onSuccess: () => {
      refetch();
      close();
      resetForm();
      setUploading(false);
      notification.success("Sponsoränderung wurde gespeichert");
    },
    onError: (error: unknown) => {
      setUploading(false);
      notification.error({
        message: (error as Error).message || "Sponsor konnte nicht aktualisiert werden",
      });
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (data: Parameters<typeof deleteSponsorFn>[0]["data"]) => deleteSponsorFn({ data }),
    onSuccess: () => {
      refetch();
      close();
      resetForm();
      notification.success("Sponsor wurde erfolgreich gelöscht");
    },
    onError: (error: unknown) => {
      notification.error({
        message: (error as Error).message || "Sponsor konnte nicht gelöscht werden",
      });
    },
  });

  const resetForm = () => {
    form.reset();
    setLogoFile(null);
    setDeleteLogo(false);
    setExpiryDate(null);
  };

  const handleSubmit = async (formData: typeof defaultFormValues) => {
    if (!formData.name) return;
    const currentEditingId = formData.id;
    const { id: _id, ...formFields } = formData;

    setUploading(true);
    try {
      let logoS3Key = formData.logoS3Key;

      // Handle logo deletion
      if (deleteLogo) {
        logoS3Key = undefined;
      }
      // Upload new logo if a file was selected
      else if (logoFile) {
        logoS3Key = await uploadImageFile(logoFile, "sponsors");
      }

      const ttl = expiryDate ? Math.floor(dayjs(expiryDate).unix()) : undefined;

      const cleanedData = Object.fromEntries(
        Object.entries({ ...formFields, logoS3Key, ttl }).filter(([key, value]) => {
          if (key === "logoS3Key" || key === "ttl") return true;
          return value !== "" && value !== undefined;
        }),
      );

      if (currentEditingId) {
        updateMutation.mutate({
          id: currentEditingId,
          data: {
            ...cleanedData,
            description: formData.description || null, // null = clear existing value
            websiteUrl: formData.websiteUrl || null, // null = clear existing value
            logoS3Key: deleteLogo ? null : logoS3Key,
            ttl: ttl ?? null,
          },
        });
      } else {
        createMutation.mutate(
          Object.fromEntries(
            Object.entries(cleanedData).filter(
              ([, value]) => value !== null && value !== undefined && value !== "",
            ),
          ) as SponsorInput,
        );
      }
    } catch (error) {
      notification.error({
        message: error instanceof Error ? error.message : "Ein Fehler ist aufgetreten",
      });
      setUploading(false);
    }
  };

  const handleEdit = (sponsor: SponsorInput & { id: string }) => {
    form.setFieldValue("id", sponsor.id);
    form.setFieldValue("name", sponsor.name);
    form.setFieldValue("description", sponsor.description || "");
    form.setFieldValue("websiteUrl", sponsor.websiteUrl || "");
    form.setFieldValue("logoS3Key", sponsor.logoS3Key);
    form.setFieldValue("ttl", sponsor.ttl);
    // Convert Unix timestamp back to Date if it exists
    setExpiryDate(sponsor.ttl ? dayjs.unix(sponsor.ttl).toDate() : null);
    setDeleteLogo(false);
    setLogoFile(null);
    open();
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Möchten Sie diesen Sponsor wirklich löschen?")) {
      deleteMutation.mutate({ id });
    }
  };

  const handleOpenNew = () => {
    resetForm();
    open();
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Sponsoren</Title>
        <Button onClick={handleOpenNew} leftSection={<Plus />} visibleFrom="sm">
          Neuer Sponsor
        </Button>
        <ActionIcon onClick={handleOpenNew} hiddenFrom="sm" variant="filled" radius="xl">
          <Plus size={20} />
        </ActionIcon>
      </Group>

      <Modal
        opened={opened}
        onClose={close}
        title={editingId ? "Sponsor bearbeiten" : "Neuer Sponsor"}
        size={isMobile ? "100%" : "lg"}
        fullScreen={isMobile}
      >
        <form.Subscribe selector={(state) => state.values}>
          {(formData) => (
            <Stack gap="md" p={{ base: "md", sm: "sm" }}>
              <form.Field name="name">
                {(field) => (
                  <TextInput
                    label="Name"
                    placeholder="z.B. Firma Mustermann"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    required
                  />
                )}
              </form.Field>
              <form.Field name="description">
                {(field) => (
                  <Textarea
                    label="Beschreibung"
                    placeholder="Optionale Beschreibung..."
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    minRows={3}
                  />
                )}
              </form.Field>
              <form.Field name="websiteUrl">
                {(field) => (
                  <TextInput
                    label="Website"
                    placeholder="https://..."
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                )}
              </form.Field>
              <DatePickerInput
                label="Ablaufdatum"
                placeholder="Optionales Ablaufdatum auswählen"
                description="Wann dieser Sponsor automatisch gelöscht werden soll"
                value={expiryDate}
                onChange={(date) => {
                  if (date) setExpiryDate(dayjs(date).toDate());
                  else setExpiryDate(null);
                }}
                clearable
                minDate={dayjs().add(1, "day").toDate()}
              />
              <CurrentLogoDisplay
                logoS3Key={formData.logoS3Key}
                logoFile={logoFile}
                deleteLogo={deleteLogo}
                onFileChange={setLogoFile}
                onDeleteToggle={() => {
                  setDeleteLogo(!deleteLogo);
                  setLogoFile(null);
                }}
                onFileSizeError={(message) => {
                  notification.error({ message });
                }}
              />{" "}
              <Group justify="space-between" mt="md">
                {editingId && (
                  <>
                    <ActionIcon
                      hiddenFrom="sm"
                      color="red"
                      variant="light"
                      onClick={() => handleDelete(editingId)}
                      loading={deleteMutation.isPending}
                      size="lg"
                    >
                      <Trash2 />
                    </ActionIcon>
                    <Button
                      visibleFrom="sm"
                      color="red"
                      variant="light"
                      onClick={() => handleDelete(editingId)}
                      loading={deleteMutation.isPending}
                    >
                      Löschen
                    </Button>
                  </>
                )}
                <Group gap="xs" ms="auto">
                  <Button variant="light" type="button" onClick={close}>
                    Abbrechen
                  </Button>
                  <Button
                    variant="filled"
                    type="button"
                    onClick={() => void handleSubmit(formData)}
                    loading={uploading || createMutation.isPending || updateMutation.isPending}
                    disabled={!formData.name}
                  >
                    {editingId ? "Aktualisieren" : "Erstellen"}
                  </Button>
                </Group>
              </Group>
            </Stack>
          )}
        </form.Subscribe>
      </Modal>

      {isLoading ? (
        <Text>Laden...</Text>
      ) : sponsors && sponsors.items.length > 0 ? (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="md">
          {sponsors.items.map((sponsor) => (
            <SponsorCard
              key={sponsor.id}
              sponsor={sponsor}
              onEdit={handleEdit}
              onDelete={handleDelete}
              isDeleting={deleteMutation.isPending}
            />
          ))}
        </SimpleGrid>
      ) : (
        <Text c="dimmed" ta="center" py="xl">
          Keine Sponsoren vorhanden
        </Text>
      )}
    </Stack>
  );
}

function SponsorCard({
  sponsor,
  onEdit,
}: {
  sponsor: SponsorInput & { id: string };
  onEdit: (sponsor: SponsorInput & { id: string }) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}) {
  const { data: logoUrl } = useQuery({
    queryKey: ["upload", "fileUrl", sponsor.logoS3Key],
    queryFn: () => getFileUrlFn({ data: { s3Key: sponsor.logoS3Key || "" } }),
    enabled: !!sponsor.logoS3Key,
  });

  // Extract domain from URL for display
  const getDomainFromUrl = (url: string) => {
    try {
      const domain = new URL(url).hostname;
      return domain.replace(/^www\./, "");
    } catch {
      return url;
    }
  };

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Card.Section withBorder bg="blumine">
        {logoUrl ? (
          <Image src={logoUrl} height={160} alt={sponsor.name} fit="contain" p="md" />
        ) : (
          <div
            style={{
              height: 160,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "var(--mantine-color-gray-1)",
            }}
          >
            <Text c="dimmed">Kein Logo</Text>
          </div>
        )}
      </Card.Section>

      <Stack gap="xs" mt="md">
        <Group justify="space-between" align="flex-start">
          <Title order={4} lineClamp={1}>
            {sponsor.name}
          </Title>
          <Group gap={8}>
            <ActionIcon
              variant="light"
              radius="xl"
              onClick={() => onEdit(sponsor)}
              aria-label="Bearbeiten"
              visibleFrom="sm"
            >
              <Pencil size={16} />
            </ActionIcon>
            <ActionIcon
              variant="light"
              radius="xl"
              onClick={() => onEdit(sponsor)}
              aria-label="Bearbeiten"
              hiddenFrom="sm"
            >
              <Pencil size={16} />
            </ActionIcon>
          </Group>
        </Group>

        {sponsor.description && (
          <Text size="sm" c="dimmed" lineClamp={2}>
            {sponsor.description}
          </Text>
        )}

        {sponsor.websiteUrl && (
          <Anchor
            href={sponsor.websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            fw={500}
          >
            <Group gap="xs">
              <Globe size={14} style={{ color: "var(--mantine-color-turquoise-2)" }} />
              {getDomainFromUrl(sponsor.websiteUrl)}
            </Group>
          </Anchor>
        )}
      </Stack>
    </Card>
  );
}

export const Route = createFileRoute("/admin/_layout/sponsors")({
  component: SponsorsPage,
});
